import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import { Checkbox } from '@/components/elements/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { InstructorCertifications } from '@/components/InstructorCertifications'
import { MultiSelect } from '@/components/MultiSelect'
import { Select } from '@/components/Select'
import { SlidePanel } from '@/components/SlidePanel'
import { usePhoneMask, cleanPhone } from '@/hooks/PhoneNumberMask'
import { getEntityJobs } from '@/services/entity.service'
import { addUser, updateUser, reinviteUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { ROLE_OPTIONS } from '@/utils/constants'
import { cn, toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import SetPasswordDialog from './SetPasswordDialog'

const DEFAULT_VALUES = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  mfaDevice: 'Email',
  isXverseAdmin: false,
  isXverseIntructor: false,
  isXverseInstructorCandidate: false,
  entityRoles: [],
}

const AddEditUserSlideout = ({
  editUser,
  entities,
  queryKey,
  resetAndClose,
  showXverseOptions = true,
}) => {
  const { user } = useContext(UserStoreContext)

  const defaultValues = useMemo(() => {
    if (!editUser) return DEFAULT_VALUES

    const {
      id,
      email,
      firstName,
      lastName,
      phoneNumber,
      mfaDevice,
      isXverseAdmin,
      isXverseInstructor,
      isXverseInstructorCandidate,
      entityRoles,
    } = editUser

    return {
      ...DEFAULT_VALUES,
      id,
      email,
      firstName,
      lastName,
      phoneNumber: phoneNumber || '',
      mfaDevice: mfaDevice || 'Email',
      isXverseAdmin,
      isXverseInstructor,
      isXverseInstructorCandidate,
      entityRoles: entityRoles?.map((role) => ({
        ...role,
        jobs: role.jobs?.map((j) => j.id) || [],
      })),
    }
  }, [editUser])

  const form = useForm({ defaultValues })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'entityRoles',
  })

  // State to cache fetched jobs per entity
  const [entityJobs, setEntityJobs] = useState({})

  // State to track reinvite status
  const [reinviteStatus, setReinviteStatus] = useState(null) // null | 'success' | 'error'
  const [reinviteErrorMessage, setReinviteErrorMessage] = useState('')

  // State to track password dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)

  const resetForm = () => {
    form.reset(DEFAULT_VALUES)
    resetAndClose()
  }

  const entityOptions = entities.map((e) => ({ ...e, label: e.name }))

  // Fetch jobs when entity is selected
  const fetchJobsForEntity = async (entityId) => {
    if (!entityId || entityJobs[entityId]) return

    try {
      const response = await getEntityJobs({ entityId })
      setEntityJobs((prev) => ({
        ...prev,
        [entityId]: response.results || [],
      }))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch jobs:', error)
    }
  }

  // Fetch jobs for existing roles on load
  useEffect(() => {
    if (editUser?.entityRoles) {
      editUser.entityRoles.forEach((role) => {
        fetchJobsForEntity(role.entity.id)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateUser } = useMutation({
    mutationFn: (data) => (editUser ? updateUser(data) : addUser(data)),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(
        `${data.firstName} ${data.lastName} ${editUser ? 'updated' : 'added'} successfully`,
      )
      resetForm()

      // Refresh admin users list
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // REINVITE user
  const { isPending: pendingReinvite, mutate: reinviteUserMutation } = useMutation({
    mutationFn: (userId) => reinviteUser(userId),
    onError: (err) => {
      setReinviteStatus('error')
      setReinviteErrorMessage(err.message)
      handleError(err.message)
    },
    onSuccess: () => {
      setReinviteStatus('success')
      handleSuccess('User reinvited successfully')

      // Refresh admin users list
      queryClient.invalidateQueries({ queryKey })
    },
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => {
    // A user needs somewhere to land. Require either the Xverse Admin flag or at
    // least one account role; otherwise the user would log in to a dead end.
    if (!data.isXverseAdmin && (!data.entityRoles || data.entityRoles.length === 0)) {
      form.setError('entityRoles', {
        type: 'manual',
        message: 'A user must be an Xverse Admin or have at least one account role.',
      })
      return
    }

    mutateUser({
      ...data,
      entityRoles: data.entityRoles.map((r) => ({
        ...r,
        entity: r.entity.id,
      })),
    })
  }

  const renderRole = (entityRole, index) => {
    const selectedEntityId = form.watch(`entityRoles.${index}.entity.id`)
    const jobOptions = entityJobs[selectedEntityId] || []

    return (
      <div className="flex flex-col gap-3 border-b py-3 last:border-0" key={entityRole.id}>
        <div className="flex flex-row items-start justify-between gap-3">
          <div className="flex flex-1 flex-col gap-3">
            {/* Account Select */}
            <FormField
              control={form.control}
              disabled={isPending}
              name={`entityRoles.${index}.entity.id`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account</FormLabel>
                  <FormControl>
                    <Select
                      id="entity"
                      placeholder="Select Account"
                      options={entityOptions}
                      {...field}
                      onChange={(value) => {
                        field.onChange(value)
                        // Clear jobs when entity changes
                        form.setValue(`entityRoles.${index}.jobs`, [])
                        if (value) {
                          fetchJobsForEntity(value)
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            {/* Roles and Jobs in horizontal row */}
            <div className="flex flex-row gap-4">
              {/* Roles MultiSelect */}
              <FormField
                control={form.control}
                disabled={isPending}
                name={`entityRoles.${index}.roles`}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Roles</FormLabel>
                    <FormControl>
                      <MultiSelect
                        id="roles"
                        placeholder="Select Roles"
                        options={ROLE_OPTIONS}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              {/* Jobs MultiSelect - only show if entity is selected */}
              {selectedEntityId && (
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name={`entityRoles.${index}.jobs`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Jobs</FormLabel>
                      <FormControl>
                        <MultiSelect
                          id="jobs"
                          placeholder="Select Jobs"
                          options={jobOptions.map((j) => ({ label: j.title, id: j.id }))}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>

          <Button variant="destructive" onClick={() => remove(index)} size="sm" type="button">
            Remove
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <SlidePanel
        actions={[
          {
            label: 'Cancel',
            onClick: () => resetAndClose(),
            type: 'cancel',
          },
          {
            label: `${editUser ? 'Update' : 'Add'} User`,
            onClick: form.handleSubmit(onSubmit),
            type: 'submit',
          },
        ]}
        content={
          <Form {...form}>
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
              {editUser && editUser.lastLogin === null && (
                <div
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border-1 p-4',
                    reinviteStatus === 'success' && 'border-green-500 bg-green-50',
                    reinviteStatus === 'error' && 'border-red-500 bg-red-50',
                    !reinviteStatus && 'bg-muted',
                  )}
                >
                  {reinviteStatus === 'success' && (
                    <span className="text-sm text-green-700">
                      Invitation email has been resent successfully!
                    </span>
                  )}
                  {reinviteStatus === 'error' && (
                    <>
                      <span className="text-sm font-semibold text-red-700">
                        Failed to resend invitation
                      </span>
                      <span className="text-sm text-red-600">{reinviteErrorMessage}</span>
                    </>
                  )}
                  {!reinviteStatus && (
                    <span className="text-sm">
                      This user has not logged in yet. You can resend the invitation email.
                    </span>
                  )}
                  {reinviteStatus !== 'success' && (
                    <ConfirmButton
                      confirmContent={`Are you sure you want to reinvite: ${formatUserName(editUser)}?`}
                      confirmTitle="Reinvite User"
                      className="self-start"
                      variant="outline"
                      loading={pendingReinvite}
                      onClick={() => reinviteUserMutation(editUser.id)}
                    >
                      Reinvite User
                    </ConfirmButton>
                  )}
                </div>
              )}

              {editUser && (
                <div className="bg-muted flex flex-col gap-4 rounded-lg border-1 p-4">
                  <span className="text-sm font-semibold">Password Management</span>
                  <span className="text-muted-foreground text-sm">
                    Set a new password for this user. They will be notified via email.
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="self-start"
                    onClick={() => setShowPasswordDialog(true)}
                  >
                    Set New Password
                  </Button>
                </div>
              )}

              <div className="flex flex-col gap-4">
                <span className="leading-6 font-bold">User Details</span>
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input id="email" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  rules={{ required: 'This field is required' }}
                />
                <div className="flex flex-row gap-4">
                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input id="firstName" type="firstName" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                    rules={{ required: 'This field is required' }}
                  />

                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input id="lastName" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                    rules={{ required: 'This field is required' }}
                  />
                </div>

                <div className="flex flex-row gap-4">
                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="phoneNumber"
                    render={({ field }) => {
                      // eslint-disable-next-line react-hooks/rules-of-hooks
                      const { masked } = usePhoneMask(field.value ?? '')

                      return (
                        <FormItem className="w-full">
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input
                              id="phoneNumber"
                              type="tel"
                              placeholder="(555) 555-1234"
                              maxLength={14}
                              inputMode="tel"
                              autoComplete="tel"
                              value={masked}
                              onChange={(e) => {
                                const raw = cleanPhone(e.target.value)
                                field.onChange(raw)
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />

                  <FormField
                    control={form.control}
                    disabled={isPending}
                    name="mfaDevice"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>MFA Device</FormLabel>
                        <FormControl>
                          <Select
                            id="mfaDevice"
                            placeholder="Select MFA Device"
                            options={[
                              { id: 'Email', label: 'Email' },
                              { id: 'Phone', label: 'Phone' },
                            ]}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {showXverseOptions && (
                <div className="flex flex-col gap-4">
                  <span className="leading-6 font-bold">Xverse Roles</span>
                  <div className="flex flex-col gap-2">
                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="isXverseAdmin"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              id="isXverseAdmin"
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked)}
                              // Do not allow user to edit their own admin status
                              disabled={editUser?.id === user?.id}
                            />
                          </FormControl>
                          <FormLabel>Xverse Admin</FormLabel>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="isXverseInstructor"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              id="isXverseInstructor"
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked)}
                            />
                          </FormControl>
                          <FormLabel>Xverse Instructor</FormLabel>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="isXverseInstructorCandidate"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center gap-2">
                          <FormControl>
                            <Checkbox
                              id="isXverseInstructorCandidate"
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked)}
                            />
                          </FormControl>
                          <FormLabel>Xverse Instructor Candidate</FormLabel>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {editUser?.isXverseInstructor && <InstructorCertifications user={editUser} />}
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div className="flex flex-row items-center justify-between">
                  <span className="leading-6 font-bold">Account Access</span>
                </div>

                {fields.length > 0 ? (
                  <div className="flex flex-col rounded-lg border-1 px-2 py-1">
                    {fields.map(renderRole)}
                  </div>
                ) : (
                  <span className="text-muted-foreground self-center text-sm">
                    No roles assigned
                  </span>
                )}

                {form.formState.errors?.entityRoles?.message &&
                  typeof form.formState.errors.entityRoles.message === 'string' && (
                    <span className="text-destructive text-sm">
                      {form.formState.errors.entityRoles.message}
                    </span>
                  )}

                <Button
                  className="grow-0 self-start"
                  variant="outline"
                  onClick={() => append({ entity: { id: null }, roles: [], jobs: [] })}
                  size="sm"
                  type="button"
                >
                  Add Role
                </Button>
              </div>
            </form>
          </Form>
        }
        loading={isPending}
        title={`${editUser ? 'Update' : 'Add'} User`}
        setOpen={resetForm}
      />

      {editUser && showPasswordDialog && (
        <SetPasswordDialog
          user={editUser}
          open={showPasswordDialog}
          setOpen={setShowPasswordDialog}
          queryKey={queryKey}
        />
      )}
    </>
  )
}

AddEditUserSlideout.propTypes = {
  editUser: PropTypes.object,
  entities: PropTypes.array.isRequired,
  queryKey: PropTypes.array.isRequired,
  resetAndClose: PropTypes.func.isRequired,
  showXverseOptions: PropTypes.bool,
}

export default AddEditUserSlideout
